import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  FilterModel,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import { useSearchParams } from 'react-router-dom';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useEmbryoFieldsEnabled } from '@/pages/embryo-app/components/EmbryoForm/full-inventory';
import { useAccessTokenRef } from '@/services/auth';
import {
  embryosBySitesQueryOptions,
  embryosFromIndexQueryOptions,
  embryosFromIndexV4QueryOptions,
  embryosFromIndexV5QueryOptions,
  ivfTransitEmbryosFromIndexV4QueryOptions,
  ivfTransitEmbryosFromIndexV5QueryOptions,
  ivfTransitEmbryosQueryOptions,
} from '@/services/embryo-service/hooks';
import { addGridQueryParams } from '@/utils/query';

import { EmbryoFilters } from './types';

export const useEmbryoDataSource = (
  filters: EmbryoFilters,
  setLoading: (loading: boolean) => void,
  options?: { isIvfTransit?: boolean },
) => {
  const [queryParams] = useSearchParams();
  const queryClient = useQueryClient();
  const isIvfTransit = options?.isIvfTransit;
  const addRemoveEmbryoFields = useEmbryoFieldsEnabled();
  const finalEmbryoDatapoints = useFeatureFlag('Final_Embryo_Datapoints');
  const isIvfTransitTotalCountEnabled = useFeatureFlag(
    'Embryo_IVF_Total_Count',
  );

  const tokenRef = useAccessTokenRef();

  const filtersRef = useRef(filters);
  const setLoadingRef = useRef(setLoading);
  useEffect(() => {
    filtersRef.current = filters;
    setLoadingRef.current = setLoading;
  }, [filters, setLoading]);

  const getRowsBySites = useCallback(
    async (params: IServerSideGetRowsParams) => {
      if (
        typeof params.request.startRow !== 'number' ||
        typeof params.request.endRow !== 'number'
      ) {
        return;
      }

      const currentFilters = filtersRef.current;
      const currentSetLoading = setLoadingRef.current;

      const siteIds = queryParams.getAll('siteId');
      if (!siteIds.length) {
        params.success({ rowData: [], rowCount: 0 });
        return;
      }

      currentSetLoading(true);

      const apiSearchParams = new URLSearchParams();
      for (const siteId of siteIds) {
        apiSearchParams.append('siteId', siteId);
      }

      if (currentFilters.searchTerm) {
        apiSearchParams.set('search', currentFilters.searchTerm);
      }
      if (currentFilters.stateCode != null) {
        apiSearchParams.set('stateCode', String(currentFilters.stateCode));
      }
      if (currentFilters.ownerId) {
        apiSearchParams.set('ownerId', currentFilters.ownerId);
      }

      const sortModel =
        params.request.sortModel.length > 0 ? params.request.sortModel : [];

      const createOptions = isIvfTransit
        ? ivfTransitEmbryosQueryOptions
        : embryosBySitesQueryOptions;

      try {
        const response = await queryClient.fetchQuery(
          createOptions(
            tokenRef.current,
            addGridQueryParams(
              apiSearchParams,
              params.request.filterModel as FilterModel | null,
              sortModel,
              params.request.startRow,
              params.request.endRow,
            ),
          ),
        );

        const startRow = params.request.startRow ?? 0;
        params.success({
          rowData: response.items,
          rowCount: response.hasMore
            ? undefined
            : startRow + response.items.length,
        });
        currentSetLoading(false);
      } catch {
        params.fail();
        currentSetLoading(false);
      }
    },
    [tokenRef, isIvfTransit, queryClient, queryParams],
  );

  // Read-model path: same multi-site inputs as getRowsBySites, but served by the
  // denormalized EmbryoSearchIndex via /api/v2/embryo/index. The index returns an exact
  // totalCount, so rowCount is set directly (no hasMore paging heuristic).
  const getRowsFromIndex = useCallback(
    async (params: IServerSideGetRowsParams) => {
      if (
        typeof params.request.startRow !== 'number' ||
        typeof params.request.endRow !== 'number'
      ) {
        return;
      }

      const currentFilters = filtersRef.current;
      const currentSetLoading = setLoadingRef.current;

      currentSetLoading(true);

      // Unlike the multi-site endpoint, the index serves the full inventory
      // when no site is selected.
      const siteIds = queryParams.getAll('siteId');
      const apiSearchParams = new URLSearchParams();
      for (const siteId of siteIds) {
        apiSearchParams.append('siteId', siteId);
      }

      if (currentFilters.searchTerm) {
        apiSearchParams.set('search', currentFilters.searchTerm);
      }
      if (currentFilters.stateCode != null) {
        apiSearchParams.set('stateCode', String(currentFilters.stateCode));
      }
      if (currentFilters.ownerId) {
        apiSearchParams.set('ownerId', currentFilters.ownerId);
      }

      const sortModel =
        params.request.sortModel.length > 0 ? params.request.sortModel : [];

      try {
        const response = await queryClient.fetchQuery(
          embryosFromIndexQueryOptions(
            addGridQueryParams(
              apiSearchParams,
              params.request.filterModel as FilterModel | null,
              sortModel,
              params.request.startRow,
              params.request.endRow,
            ),
          ),
        );

        params.success({
          rowData: response.items,
          rowCount: response.totalCount,
        });
        currentSetLoading(false);
      } catch {
        params.fail();
        currentSetLoading(false);
      }
    },
    [queryClient, queryParams],
  );

  const getRowsFromIndexV4 = useCallback(
    async (params: IServerSideGetRowsParams) => {
      if (
        typeof params.request.startRow !== 'number' ||
        typeof params.request.endRow !== 'number'
      ) {
        return;
      }

      const currentFilters = filtersRef.current;
      const currentSetLoading = setLoadingRef.current;

      currentSetLoading(true);

      const siteIds = queryParams.getAll('siteId');
      const apiSearchParams = new URLSearchParams();
      for (const siteId of siteIds) {
        apiSearchParams.append('siteId', siteId);
      }

      if (currentFilters.searchTerm) {
        apiSearchParams.set('search', currentFilters.searchTerm);
      }
      if (currentFilters.stateCode != null) {
        apiSearchParams.set('stateCode', String(currentFilters.stateCode));
      }
      if (currentFilters.ownerId) {
        apiSearchParams.set('ownerId', currentFilters.ownerId);
      }

      const sortModel =
        params.request.sortModel.length > 0 ? params.request.sortModel : [];

      try {
        const response = await queryClient.fetchQuery(
          embryosFromIndexV4QueryOptions(
            tokenRef.current,
            addGridQueryParams(
              apiSearchParams,
              params.request.filterModel as FilterModel | null,
              sortModel,
              params.request.startRow,
              params.request.endRow,
            ),
          ),
        );

        params.success({
          rowData: response.items,
          rowCount: response.totalCount,
        });
        currentSetLoading(false);
      } catch {
        params.fail();
        currentSetLoading(false);
      }
    },
    [tokenRef, queryClient, queryParams],
  );

  const getRowsFromIvfTransitIndex = useCallback(
    async (params: IServerSideGetRowsParams) => {
      if (
        typeof params.request.startRow !== 'number' ||
        typeof params.request.endRow !== 'number'
      ) {
        return;
      }

      const currentFilters = filtersRef.current;
      const currentSetLoading = setLoadingRef.current;
      const siteIds = queryParams.getAll('siteId');
      if (!siteIds.length) {
        params.success({ rowData: [], rowCount: 0 });
        return;
      }

      currentSetLoading(true);
      const apiSearchParams = new URLSearchParams();
      for (const siteId of siteIds) {
        apiSearchParams.append('siteId', siteId);
      }

      if (currentFilters.searchTerm) {
        apiSearchParams.set('search', currentFilters.searchTerm);
      }
      if (currentFilters.stateCode != null) {
        apiSearchParams.set('stateCode', String(currentFilters.stateCode));
      }
      if (currentFilters.ownerId) {
        apiSearchParams.set('ownerId', currentFilters.ownerId);
      }

      const sortModel =
        params.request.sortModel.length > 0 ? params.request.sortModel : [];

      try {
        const response = await queryClient.fetchQuery(
          ivfTransitEmbryosFromIndexV4QueryOptions(
            tokenRef.current,
            addGridQueryParams(
              apiSearchParams,
              params.request.filterModel as FilterModel | null,
              sortModel,
              params.request.startRow,
              params.request.endRow,
            ),
          ),
        );

        params.success({
          rowData: response.items,
          rowCount: response.totalCount,
        });
        currentSetLoading(false);
      } catch {
        params.fail();
        currentSetLoading(false);
      }
    },
    [tokenRef, queryClient, queryParams],
  );

  const fetchGridV5Page = useCallback(
    async (
      params: IServerSideGetRowsParams,
      optionsFactory: typeof embryosFromIndexV5QueryOptions,
    ) => {
      if (
        typeof params.request.startRow !== 'number' ||
        typeof params.request.endRow !== 'number'
      ) {
        return;
      }

      const currentFilters = filtersRef.current;
      const currentSetLoading = setLoadingRef.current;

      currentSetLoading(true);

      const siteIds = queryParams.getAll('siteId');
      const apiSearchParams = new URLSearchParams();
      for (const siteId of siteIds) {
        apiSearchParams.append('siteId', siteId);
      }

      if (currentFilters.searchTerm) {
        apiSearchParams.set('search', currentFilters.searchTerm);
      }
      if (currentFilters.stateCode != null) {
        apiSearchParams.set('stateCode', String(currentFilters.stateCode));
      }
      if (currentFilters.ownerId) {
        apiSearchParams.set('ownerId', currentFilters.ownerId);
      }

      const sortModel =
        params.request.sortModel.length > 0 ? params.request.sortModel : [];

      try {
        const response = await queryClient.fetchQuery(
          optionsFactory(
            tokenRef.current,
            addGridQueryParams(
              apiSearchParams,
              params.request.filterModel as FilterModel | null,
              sortModel,
              params.request.startRow,
              params.request.endRow,
            ),
          ),
        );

        params.success({
          rowData: response.items,
          rowCount: response.totalCount,
        });
        currentSetLoading(false);
      } catch {
        params.fail();
        currentSetLoading(false);
      }
    },
    [tokenRef, queryClient, queryParams],
  );

  const getRowsFromIndexV5 = useCallback(
    async (params: IServerSideGetRowsParams) => {
      await fetchGridV5Page(params, embryosFromIndexV5QueryOptions);
    },
    [fetchGridV5Page],
  );

  const getRowsFromIvfTransitIndexV5 = useCallback(
    async (params: IServerSideGetRowsParams) => {
      await fetchGridV5Page(params, ivfTransitEmbryosFromIndexV5QueryOptions);
    },
    [fetchGridV5Page],
  );

  const dataSource: IServerSideDatasource = useMemo(
    () => ({
      getRows: isIvfTransit
        ? isIvfTransitTotalCountEnabled
          ? finalEmbryoDatapoints
            ? getRowsFromIvfTransitIndexV5
            : getRowsFromIvfTransitIndex
          : getRowsBySites
        : finalEmbryoDatapoints
          ? getRowsFromIndexV5
          : addRemoveEmbryoFields
            ? getRowsFromIndexV4
            : getRowsFromIndex,
    }),
    [
      addRemoveEmbryoFields,
      finalEmbryoDatapoints,
      getRowsBySites,
      getRowsFromIndex,
      getRowsFromIndexV4,
      getRowsFromIndexV5,
      getRowsFromIvfTransitIndex,
      getRowsFromIvfTransitIndexV5,
      isIvfTransit,
      isIvfTransitTotalCountEnabled,
    ],
  );

  return { dataSource };
};
